import React, { useState } from "react";
import { BlogPost } from "../../const/types";
import {
  Box,
  HStack,
  IconButton,
  Image,
  SimpleGrid,
  Text,
  VStack,
  Link as Chakralink,
  Icon,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Select,
  Button,
  useToast,
  Heading,
  Badge,
} from "@chakra-ui/react";
import formatDate from "../../lib/formatDate";
import {
  FacebookLogo,
  FloppyDisk,
  InstagramLogo,
  TwitterLogo,
  YoutubeLogo,
} from "@phosphor-icons/react";
import { useFormik } from "formik";
import RequiredForm from "../RequiredForm";
import cr from "../../lib/request";
import BlogFileUploader from "../FileUploader";
import DeleteBlogPostModal from "./DeleteBlogPostModal";
import * as yup from "yup";
import Editor from "./PostEditor";
import useBlogPost from "../../globalState/usePost";
import SwitchBlogOnlineStatus from "./SwitchBlogPostOnlineStatus";
import BlogPostOnlineStatus from "./BlogPostOnlineStatus";
import Textarea from "../Textarea";
import DOMPurify from "dompurify";

type Props = {
  data: BlogPost;
  categories: any[];
};

export default function BlogPostEditor({ data, categories }: Props) {
  const [mainImagePreviewUrl, setMainImagePreviewUrl] = useState<string | null>(
    null
  );
  const { post } = useBlogPost();
  const [loading, setLoading] = useState<boolean>(false);
  const toast = useToast();

  const formik = useFormik({
    validateOnChange: false,

    initialValues: {
      title: data.title,
      category: data.category_id,
      description: data.description,
      image: [] as File[] | null,
    },

    validationSchema: yup.object().shape({
      title: yup.string().required("Judul Blog harus diisi"),
      category: yup.number().required("Kategori Blog harus dipilih"),
      description: yup.string().required("Deskripsi Singkat Blog harus diisi"),
      image: yup
        .array()
        .test(
          "fileType",
          "Hanya file JPG, PNG, HEIC yang diperbolehkan",
          (value) =>
            value &&
            value.every(
              (file) =>
                file.type === "image/jpeg" ||
                file.type === "image/jpg" ||
                file.type === "image/png" ||
                file.type === "image/heic"
            )
        ),
    }),

    onSubmit: (values, { resetForm }) => {
      console.log(values);
      const payload = new FormData();
      payload.append("title", values.title);
      payload.append("category", values.category.toString());
      payload.append("description", values.description);
      if (values.image) {
        values.image.forEach((file) => {
          payload.append("image", file);
        });
      }
      payload.append("post", post);

      console.log(payload);

      setLoading(true);
      const url = `api/blog/post/${data.id}/updatepost`;
      cr.post(url, payload)
        .then((r) => {
          if (r.status === 200) {
            toast({
              title: r.data.message,
              status: "success",
              duration: 5000,
              isClosable: true,
            });
          }
        })
        .catch((error) => {
          console.log(error);
          toast({
            title: error?.response?.data?.message || "Error",
            status: "error",
            duration: 5000,
            isClosable: true,
          });
        })
        .finally(() => {
          setLoading(false);
        });
    },
  });

  return (
    <VStack align={"stretch"} gap={0}>
      <HStack
        w={"100%"}
        h={"50px"}
        py={2}
        px={5}
        pl={"calc(60px + 16px)"}
        borderBottom={"1px solid var(--divider3)"}
        justify={"space-between"}
        position={"fixed"}
        top={"50px"}
        left={0}
        bg={"whiteAlpha.900"}
        backdropFilter={"blur(5px)"}
      >
        <BlogPostOnlineStatus id={data.id} />

        <HStack>
          <DeleteBlogPostModal data={data} />

          <SwitchBlogOnlineStatus id={data.id} />

          <Button
            colorScheme="p"
            className="clicky"
            size={"sm"}
            isLoading={loading}
            type="submit"
            form="editBlogPostForm"
            leftIcon={<Icon as={FloppyDisk} fontSize={18} weight="fill" />}
          >
            Save
          </Button>
        </HStack>
      </HStack>

      <SimpleGrid
        columns={[1, null, 2]}
        minH={["", null, "calc(100vh - 50px)"]}
        h={["calc(100vh - 50px)", "", null]}
        pt={"50px"}
      >
        <VStack
          align={"stretch"}
          borderRight={["", null, "1px solid var(--divider3)"]}
          borderBottom={["10px solid var(--divider3)", null, ""]}
          p={5}
          overflowY={"auto"}
        >
          <form id="editBlogPostForm" onSubmit={formik.handleSubmit}>
            <FormControl mb={4} isInvalid={formik.errors.title ? true : false}>
              <FormLabel>
                Judul
                <RequiredForm />
              </FormLabel>
              <Textarea
                name="title"
                placeholder="Masukkan judul blog"
                formik={formik}
              />
              <FormErrorMessage>{formik.errors.title}</FormErrorMessage>
            </FormControl>

            <FormControl
              mb={4}
              isInvalid={formik.errors.category ? true : false}
            >
              <FormLabel>
                Kategori
                <RequiredForm />
              </FormLabel>
              <Select
                name="category"
                placeholder="Pilih kategori blog"
                onChange={formik.handleChange}
                value={formik.values.category}
              >
                {categories?.map((category, i) => (
                  <option key={i} value={category.id}>
                    {category?.name}
                  </option>
                ))}
              </Select>
              <FormErrorMessage>{formik.errors.category}</FormErrorMessage>
            </FormControl>

            <FormControl
              mb={4}
              isInvalid={formik.errors.description ? true : false}
            >
              <FormLabel>
                Deksripsi
                <RequiredForm />
              </FormLabel>
              <Textarea
                name="description"
                placeholder="Masukkan deskripsi singkat blog"
                formik={formik}
              />
              <FormErrorMessage>{formik.errors.description}</FormErrorMessage>
            </FormControl>

            <FormControl mb={4} isInvalid={formik.errors.image ? true : false}>
              <FormLabel>Gambar Thumbnail</FormLabel>
              <BlogFileUploader
                name={"image"}
                formik={formik}
                setPreviewUrl={setMainImagePreviewUrl}
              />
              <FormErrorMessage>{formik.errors.image}</FormErrorMessage>
            </FormControl>

            <FormLabel>Post</FormLabel>
            <Editor data={data.post} />
          </form>
        </VStack>

        {/* Preview */}
        <VStack align={"stretch"} p={5} overflowY={"auto"}>
          <VStack my={5}>
            <Image src={"/images/logo.png"} h={"50px"} borderRadius={"full"} />
          </VStack>

          <Text mb={4} textAlign={"center"} opacity={0.8}>
            {formatDate(data.created_at, {
              weekday: "long",
              day: "numeric",
              month: "long",
              year: "numeric",
            })}
          </Text>

          <Heading
            as={"h1"}
            fontSize={28}
            fontWeight={700}
            textAlign={"center"}
            lineHeight={1.3}
            mb={8}
          >
            {formik.values.title}
          </Heading>

          <Image
            src={
              mainImagePreviewUrl ||
              `${process.env.REACT_APP_MEDIA_BASE_URL}${data.image}`
            }
            borderRadius={8}
            mb={2}
            objectFit={"cover"}
            aspectRatio={"16/10"}
          />

          <Badge w={"fit-content"} mb={4} colorScheme="ap">
            {data.category_name}
          </Badge>

          <SimpleGrid columns={[1, null, 2]} gap={4}>
            <HStack gap={4} align={"flex-start"}>
              <Box>
                <Text fontWeight={500}>{"Admin Kubu.id"}</Text>
                <Text
                  fontSize={"small"}
                  opacity={0.5}
                >{`Terakhir diperbarui ${formatDate(data.updated_at, {
                  day: "numeric",
                  month: "numeric",
                  year: "numeric",
                })}`}</Text>
              </Box>
            </HStack>

            <HStack
              justify={["flex-start", null, "flex-end"]}
              align={"flex-start"}
            >
              <IconButton
                aria-label="sosmedButton"
                as={Chakralink}
                isExternal
                href={data?.sosmeds?.[0].content}
                icon={
                  <Icon
                    as={FacebookLogo}
                    fontSize={[20, null, 22]}
                    weight="fill"
                  />
                }
                variant={"ghost"}
                className="btn"
                colorScheme="ap"
                borderRadius={"full"}
              />

              <IconButton
                aria-label="sosmedButton"
                as={Chakralink}
                isExternal
                href={data?.sosmeds?.[1].content}
                icon={
                  <Icon
                    as={TwitterLogo}
                    fontSize={[20, null, 22]}
                    weight="fill"
                  />
                }
                variant={"ghost"}
                className="btn"
                colorScheme="ap"
                borderRadius={"full"}
              />

              <IconButton
                aria-label="sosmedButton"
                as={Chakralink}
                isExternal
                href={data?.sosmeds?.[2].content}
                icon={
                  <Icon
                    as={InstagramLogo}
                    fontSize={[20, null, 22]}
                    weight="fill"
                  />
                }
                variant={"ghost"}
                className="btn"
                colorScheme="ap"
                borderRadius={"full"}
              />

              <IconButton
                aria-label="sosmedButton"
                as={Chakralink}
                isExternal
                href={data?.sosmeds?.[3].content}
                icon={
                  <Icon
                    as={YoutubeLogo}
                    fontSize={[20, null, 22]}
                    weight="fill"
                  />
                }
                variant={"ghost"}
                className="btn"
                colorScheme="ap"
                borderRadius={"full"}
              />
            </HStack>
          </SimpleGrid>

          <VStack
            id="thepost"
            align={"stretch"}
            borderTop={"1px solid var(--divider3)"}
            pt={8}
            mt={6}
          >
            <Text mb={4}>{formik.values.description}</Text>

            <div
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(post) }}
            />
          </VStack>
        </VStack>
      </SimpleGrid>
    </VStack>
  );
}
